// frontend/src/pages/Analytics.js

import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axiosInstance from '../utils/axiosInstance';
import '../styles/Pages.css';
import '../styles/Analytics.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

function Analytics() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsResponse, predictionsResponse] = await Promise.all([
          axiosInstance.get('/analytics/stats'),
          axiosInstance.get('/leads/predictions'),
        ]);

        setAnalyticsData(analyticsResponse.data);
        setPredictions(predictionsResponse.data);
        setError('');
      } catch (err) {
        setError('Failed to load analytics or predictions data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className="spinner">Loading...</div>;
  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const chartData = {
    labels: analyticsData?.monthlyData?.map((item) => item.month) || [],
    datasets: [
      {
        label: 'Leads',
        data: analyticsData?.monthlyData?.map((item) => item.count) || [],
        backgroundColor: 'rgba(40, 167, 69, 0.5)',
        borderColor: '#28a745',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}`,
        },
      },
      legend: {
        display: true,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Leads',
        },
      },
    },
  };

  return (
    <div className="analytics-container">
      <h1 className="page-title">Analytics Overview</h1>

      <div className="analytics-metrics">
        <div className="metric-box">Total Leads: <strong>{analyticsData.totalLeads}</strong></div>
        <div className="metric-box">Total Loan Amount: <strong>${analyticsData.totalLoanAmount.toLocaleString()}</strong></div>
        <div className="metric-box">Average Loan Amount: <strong>${analyticsData.averageLoanAmount.toLocaleString()}</strong></div>
        <div className="metric-box">Converted Leads: <strong>{analyticsData.totalConvertedLeads}</strong></div>
        <div className="metric-box">Conversion Rate: <strong>{analyticsData.conversionRate}%</strong></div>
      </div>

      <div className="chart-section">
        <h2>{chartData.labels.length > 0 ? 'Leads Over Time' : 'No Data Available for Leads Over Time'}</h2>
        {chartData.labels.length > 0 ? (
          <div className="chart-wrapper">
            <Bar data={chartData} options={chartOptions} />
          </div>
        ) : (
          <p className="no-data">No data available for chart.</p>
        )}
      </div>

      <div className="predictions-section">
        <h2>AI-Driven Lead Predictions</h2>
        {predictions.length > 0 ? (
          <div className="prediction-table-wrapper">
            <table className="predictions-table">
              <thead>
                <tr>
                  <th>Lead Name</th>
                  <th>Predicted Conversion Rate (%)</th>
                </tr>
              </thead>
              <tbody>
                {predictions.map((prediction) => (
                  <tr key={prediction.id}>
                    <td>{prediction.name}</td>
                    <td>{prediction.predictedConversionRate.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">No predictions available at the moment.</p>
        )}
      </div>
    </div>
  );
}

export default Analytics;